import { c as _c } from "react/compiler-runtime";
import { useEffect, useEffectEvent } from 'react';
import { useAppDispatch, useAppSelector } from '@/store/hooks';
import { buscarExecucoes, buscarPassos, passosDaExecucaoFechados } from '../store/actions';
import { selectExecucoesDaSimulacao, selectMarcoDosLogs, selectPassosDaExecucao, selectSimulacaoAtual, selectSuperficieDaSimulacao, selectUltimoSeqDaSimulacao } from '../store/selectors';
import type { LogsDaSimulacao, PassosNoEstado } from '../types';

/**
 * A última página pode ter vindo incompleta e ganhado passos na rodada seguinte (a releitura da
 * página 1 então liga o `hasNext` pelo total novo): ela é relida, e não pulada para uma vazia.
 */
const proximaPaginaDosPassos = ({
  itens,
  pageSize
}: PassosNoEstado) => Math.floor(itens.length / pageSize) + 1;

/**
 * Os logs de execução da simulação mostrada (CA-7, CA-23): a primeira página ao abrir e de novo
 * a cada evento novo do polling ou comando que terminou. "Carregar mais" segue o cursor das
 * execuções e a página dos passos.
 */
export function useLogsDaSimulacao() {
  const $ = _c(48);
  const dispatch = useAppDispatch();
  const superficie = useAppSelector(selectSuperficieDaSimulacao);
  const simulacaoId = useAppSelector(selectSimulacaoAtual)?.id ?? null;
  const ultimoSeq = useAppSelector(selectUltimoSeqDaSimulacao);
  const marco = useAppSelector(selectMarcoDosLogs);
  const execucoes = useAppSelector(selectExecucoesDaSimulacao);
  const passos = useAppSelector(selectPassosDaExecucao);
  const visivel = superficie === "ADMIN";
  let t0;
  if ($[0] !== dispatch || $[1] !== passos.execucaoId) {
    t0 = () => {
      dispatch(buscarExecucoes({
        cursor: null
      }));
      if (passos.execucaoId) {
        dispatch(buscarPassos({
          execucaoId: passos.execucaoId,
          page: 1
        }));
      }
    };
    $[0] = dispatch;
    $[1] = passos.execucaoId;
    $[2] = t0;
  } else {
    t0 = $[2];
  }
  const recarregarExecucoes = t0;
  const recarregarLogs = useEffectEvent(recarregarExecucoes);
  let t1;
  if ($[3] !== recarregarLogs || $[4] !== simulacaoId || $[5] !== visivel) {
    t1 = () => {
      if (visivel && simulacaoId) {
        recarregarLogs();
      }
    };
    $[3] = recarregarLogs;
    $[4] = simulacaoId;
    $[5] = visivel;
    $[6] = t1;
  } else {
    t1 = $[6];
  }
  let t2;
  if ($[7] !== marco || $[8] !== simulacaoId || $[9] !== ultimoSeq || $[10] !== visivel) {
    t2 = [visivel, simulacaoId, ultimoSeq, marco];
    $[7] = marco;
    $[8] = simulacaoId;
    $[9] = ultimoSeq;
    $[10] = visivel;
    $[11] = t2;
  } else {
    t2 = $[11];
  }
  useEffect(t1, t2);
  let t3;
  if ($[12] !== dispatch || $[13] !== execucoes.carregando || $[14] !== execucoes.hasMore || $[15] !== execucoes.nextCursor) {
    t3 = () => {
      if (execucoes.carregando || !execucoes.hasMore || !execucoes.nextCursor) {
        return;
      }
      dispatch(buscarExecucoes({
        cursor: execucoes.nextCursor
      }));
    };
    $[12] = dispatch;
    $[13] = execucoes.carregando;
    $[14] = execucoes.hasMore;
    $[15] = execucoes.nextCursor;
    $[16] = t3;
  } else {
    t3 = $[16];
  }
  const carregarMaisExecucoes = t3;
  let t4;
  if ($[17] !== dispatch || $[18] !== passos.execucaoId) {
    t4 = execucaoId => {
      if (passos.execucaoId === execucaoId) {
        dispatch(passosDaExecucaoFechados());
        return;
      }
      dispatch(buscarPassos({
        execucaoId,
        page: 1
      }));
    };
    $[17] = dispatch;
    $[18] = passos.execucaoId;
    $[19] = t4;
  } else {
    t4 = $[19];
  }
  const alternarExecucao = t4;
  let t5;
  if ($[20] !== dispatch || $[21] !== passos) {
    t5 = () => {
      if (!passos.execucaoId || passos.carregando || !passos.hasNext) {
        return;
      }
      dispatch(buscarPassos({
        execucaoId: passos.execucaoId,
        page: proximaPaginaDosPassos(passos)
      }));
    };
    $[20] = dispatch;
    $[21] = passos;
    $[22] = t5;
  } else {
    t5 = $[22];
  }
  const carregarMaisPassos = t5;
  let t6;
  if ($[23] !== dispatch || $[24] !== passos.execucaoId) {
    t6 = () => {
      if (passos.execucaoId) {
        dispatch(buscarPassos({
          execucaoId: passos.execucaoId,
          page: 1
        }));
      }
    };
    $[23] = dispatch;
    $[24] = passos.execucaoId;
    $[25] = t6;
  } else {
    t6 = $[25];
  }
  const recarregarPassos = t6;
  const t7 = simulacaoId !== null;
  let t8;
  if ($[26] !== execucoes.itens || $[27] !== execucoes.simulacaoId || $[28] !== simulacaoId) {
    t8 = execucoes.simulacaoId === simulacaoId ? execucoes.itens : [];
    $[26] = execucoes.itens;
    $[27] = execucoes.simulacaoId;
    $[28] = simulacaoId;
    $[29] = t8;
  } else {
    t8 = $[29];
  }
  let t9;
  if ($[30] !== alternarExecucao || $[31] !== carregarMaisExecucoes || $[32] !== carregarMaisPassos || $[33] !== execucoes.carregando || $[34] !== execucoes.erro || $[35] !== execucoes.hasMore || $[36] !== passos.carregando || $[37] !== passos.erro || $[38] !== passos.execucaoId || $[39] !== passos.hasNext || $[40] !== passos.itens || $[41] !== passos.total || $[42] !== recarregarExecucoes || $[43] !== recarregarPassos || $[44] !== t7 || $[45] !== t8 || $[46] !== visivel) {
    t9 = {
      visivel,
      iniciada: t7,
      execucoes: t8,
      carregandoExecucoes: execucoes.carregando,
      erroDasExecucoes: execucoes.erro,
      haMaisExecucoes: execucoes.hasMore,
      execucaoAberta: passos.execucaoId,
      passos: passos.itens,
      totalDePassos: passos.total,
      carregandoPassos: passos.carregando,
      erroDosPassos: passos.erro,
      haMaisPassos: passos.hasNext,
      recarregarExecucoes,
      carregarMaisExecucoes,
      alternarExecucao,
      carregarMaisPassos,
      recarregarPassos
    };
    $[30] = alternarExecucao;
    $[31] = carregarMaisExecucoes;
    $[32] = carregarMaisPassos;
    $[33] = execucoes.carregando;
    $[34] = execucoes.erro;
    $[35] = execucoes.hasMore;
    $[36] = passos.carregando;
    $[37] = passos.erro;
    $[38] = passos.execucaoId;
    $[39] = passos.hasNext;
    $[40] = passos.itens;
    $[41] = passos.total;
    $[42] = recarregarExecucoes;
    $[43] = recarregarPassos;
    $[44] = t7;
    $[45] = t8;
    $[46] = visivel;
    $[47] = t9;
  } else {
    t9 = $[47];
  }
  return t9;
}